import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  LinkOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useInfluencerLinks } from '../hooks/useInfluencerLinks';
import {
  Campaign,
  CampaignChannel,
} from '../../api/services/marketingService';

const { Title, Text } = Typography;
const ORANGE = '#fe5a1d';

const CHANNELS: CampaignChannel[] = [
  'INFLUENCER',
  'META_ADS',
  'GOOGLE_ADS',
  'TIKTOK_ADS',
  'INSTAGRAM',
  'YOUTUBE',
  'OTHER',
];

const CHANNEL_COLORS: Record<CampaignChannel, string> = {
  INFLUENCER: 'orange',
  META_ADS: 'blue',
  GOOGLE_ADS: 'green',
  TIKTOK_ADS: 'magenta',
  INSTAGRAM: 'purple',
  YOUTUBE: 'red',
  OTHER: 'default',
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const InfluencerLinks: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  // Safe when the modal (and its Form) is unmounted — returns undefined.
  const watchedSlug = Form.useWatch('slug', form);
  const {
    campaigns,
    loading,
    modalVisible,
    editing,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch,
  } = useInfluencerLinks();

  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://autoplace.al';

  useEffect(() => {
    if (modalVisible && editing) {
      form.setFieldsValue({
        name: editing.name,
        slug: editing.slug,
        channel: editing.channel,
        notes: editing.notes ?? '',
        active: editing.status === 'ACTIVE',
      });
    } else if (modalVisible && !editing) {
      form.resetFields();
      form.setFieldsValue({ active: true, channel: 'INFLUENCER' });
    }
  }, [modalVisible, editing, form]);

  const onValuesChange = (changed: Record<string, unknown>) => {
    // Auto-suggest a slug from the name while creating, until the user edits it.
    if (changed.name !== undefined && !editing) {
      const current = form.getFieldValue('slug');
      if (!current) form.setFieldValue('slug', slugify(String(changed.name)));
    }
  };

  const onFinish = (values: {
    name: string;
    slug: string;
    channel: CampaignChannel;
    notes?: string;
    active: boolean;
  }) => {
    const payload = {
      name: values.name,
      slug: values.slug,
      channel: values.channel,
      notes: values.notes,
      status: values.active ? ('ACTIVE' as const) : ('INACTIVE' as const),
    };
    if (editing) handleUpdate(editing.id, payload);
    else handleCreate(payload);
  };

  const copyLink = async (slug: string) => {
    const url = `${origin}/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      message.success(t('admin.marketing.link_copied'));
    } catch {
      message.error(url);
    }
  };

  const columns = [
    {
      title: t('admin.marketing.col_name'),
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => (
        <Space>
          <LinkOutlined style={{ color: ORANGE }} />
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.marketing.col_url'),
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => (
        <Space>
          <Text code>{`${origin.replace(/^https?:\/\//, '')}/${slug}`}</Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyLink(slug)}
          />
        </Space>
      ),
    },
    {
      title: t('admin.marketing.col_channel'),
      dataIndex: 'channel',
      key: 'channel',
      render: (v: CampaignChannel) => (
        <Tag color={CHANNEL_COLORS[v]}>{v.replace(/_/g, ' ')}</Tag>
      ),
    },
    {
      title: t('admin.marketing.col_visits'),
      dataIndex: 'visitCount',
      key: 'visitCount',
      render: (v: number) => <Text>{v ?? 0}</Text>,
    },
    {
      title: t('admin.marketing.col_status'),
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => (
        <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>
          {v === 'ACTIVE'
            ? t('admin.marketing.status_active')
            : t('admin.marketing.status_inactive')}
        </Tag>
      ),
    },
    {
      title: t('admin.marketing.col_created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <Text type="secondary">{new Date(v).toLocaleDateString()}</Text>
      ),
    },
    {
      title: t('admin.marketing.col_actions'),
      key: 'actions',
      render: (_: unknown, record: Campaign) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            style={{ color: ORANGE }}
          />
          <Popconfirm
            title={t('admin.marketing.delete_confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.marketing.btn_delete')}
            okType="danger"
            cancelText={t('admin.marketing.btn_cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('admin.marketing.links_title')}
          </Title>
          <Text type="secondary">{t('admin.marketing.links_subtitle')}</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={refetch}
            loading={loading}
            className="admin-filter-btn"
          >
            {t('admin.marketing.btn_refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="ant-btn-primary admin-filter-btn admin-filter-btn-primary"
          >
            {t('admin.marketing.btn_add')}
          </Button>
        </Space>
      </div>

      <Card bordered={false} className="admin-table-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={campaigns}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('admin.marketing.empty_text') }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <LinkOutlined style={{ color: 'var(--admin-primary)' }} />
            {editing
              ? t('admin.marketing.modal_edit_title')
              : t('admin.marketing.modal_add_title')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        okText={
          editing
            ? t('admin.marketing.modal_save')
            : t('admin.marketing.modal_create')
        }
        okButtonProps={{ className: 'ant-btn-primary' }}
        cancelText={t('admin.marketing.btn_cancel')}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="name"
            label={t('admin.marketing.label_name')}
            rules={[
              { required: true, message: t('admin.marketing.validation_name') },
            ]}
          >
            <Input placeholder={t('admin.marketing.placeholder_name')} />
          </Form.Item>
          <Form.Item
            name="slug"
            label={t('admin.marketing.label_slug')}
            rules={[
              { required: true, message: t('admin.marketing.validation_slug') },
              {
                pattern: /^[a-z0-9-]{2,40}$/,
                message: t('admin.marketing.validation_slug_format'),
              },
            ]}
            extra={`${origin.replace(/^https?:\/\//, '')}/${watchedSlug || 'your-slug'}`}
          >
            <Input
              addonBefore={`${origin.replace(/^https?:\/\//, '')}/`}
              placeholder="john"
            />
          </Form.Item>
          <Form.Item
            name="channel"
            label={t('admin.marketing.label_channel')}
            rules={[{ required: true }]}
          >
            <Select
              options={CHANNELS.map((c) => ({
                value: c,
                label: c.replace(/_/g, ' '),
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('admin.marketing.label_notes')}>
            <Input.TextArea
              rows={3}
              maxLength={1000}
              placeholder={t('admin.marketing.placeholder_notes')}
            />
          </Form.Item>
          <Form.Item
            name="active"
            label={t('admin.marketing.label_active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default InfluencerLinks;
